import { useCallback, useEffect, useMemo, useState } from 'react';
import { ManagementApi } from '../api/client';
import { Drawer } from '../components/Drawer';
import { RefreshActionButton } from '../components/RefreshActionButton';
import { defaultDirtyDraftGuardMessage, useDirtyDraftGuard } from '../configEditor';
import { PropertyTable, ResourceHeader, StatusStrip, Tabs } from '../components/ResourceDetail';
import { useI18n } from '../i18n';
import { redactedJson } from '../lib/redaction';
import { PersonaStructuredConfigEditor, compactPersonaConfigSpec, type PersonaConfigSpec } from './PersonaStructuredConfigEditor';
import { buildPersonaWorkbenchModel, canRunPersonaWorkbenchAction, type PersonaWorkbenchAction, type PersonaWorkbenchActionKey, type PersonaWorkbenchConfirmation, type PersonaWorkbenchModel, type PersonaWorkbenchSources } from './model';

type TabId = 'overview' | 'configuration' | 'runtime' | 'history';
type DrawerId = 'prompt' | 'json' | 'snapshot' | 'session' | 'config' | 'contracts' | 'preview' | 'validation' | 'publish' | null;
type ConfirmAction = PersonaWorkbenchConfirmation | null;
type RunnableAction = Extract<PersonaWorkbenchActionKey, 'validate' | 'preview' | 'create_version' | 'publish'>;
type Translate = (key: string, values?: Record<string, string | number>) => string;
type ActionRunOptions = { targetVersion?: number };

type PersonaConfigVersionSummary = {
  version: number;
  title: string;
  summary: string;
  meta: string[];
};

type LoadState = {
  loading: boolean;
  sources: PersonaWorkbenchSources;
  model: PersonaWorkbenchModel;
  errors: Record<string, string>;
  actionMessage: string;
};

const tabs: Array<{ id: TabId; label: string }> = [
  { id: 'overview', label: 'Overview' },
  { id: 'configuration', label: 'Configuration' },
  { id: 'runtime', label: 'Runtime' },
  { id: 'history', label: 'History' },
];

const configCenterRowKeys: Record<string, string> = {
  'Agent type': 'modules.personaWorkbench.configCenter.rows.agentType',
  'Runtime provider/model': 'modules.personaWorkbench.configCenter.rows.runtimeProviderModel',
  'Provider catalog': 'modules.personaWorkbench.configCenter.rows.providerCatalog',
  Thinking: 'modules.personaWorkbench.configCenter.rows.thinking',
  'Token budget': 'modules.personaWorkbench.configCenter.rows.tokenBudget',
  'Persona style': 'modules.personaWorkbench.configCenter.rows.personaStyle',
  'Display mode': 'modules.personaWorkbench.configCenter.rows.displayMode',
  'Route debug': 'modules.personaWorkbench.configCenter.rows.routeDebug',
  'Platform display overrides': 'modules.personaWorkbench.configCenter.rows.platformDisplayOverrides',
  'Disabled commands': 'modules.personaWorkbench.configCenter.rows.disabledCommands',
  'Disabled built-in prompts': 'modules.personaWorkbench.configCenter.rows.disabledBuiltinPrompts',
  'Session cleanup': 'modules.personaWorkbench.configCenter.rows.sessionCleanup',
  'Skill bindings': 'modules.personaWorkbench.configCenter.rows.skillBindings',
  'Prompt bindings': 'modules.personaWorkbench.configCenter.rows.promptBindings',
  'MCP bindings': 'modules.personaWorkbench.configCenter.rows.mcpBindings',
  'Skill binding controls': 'modules.personaWorkbench.configCenter.rows.skillBindingControls',
  'MCP binding controls': 'modules.personaWorkbench.configCenter.rows.mcpBindingControls',
  'Platform bindings': 'modules.personaWorkbench.configCenter.rows.platformBindings',
  'Available artifacts': 'modules.personaWorkbench.configCenter.rows.availableArtifacts',
  'Default runtime': 'modules.personaWorkbench.configCenter.rows.defaultRuntime',
};

const runtimeEvidenceRowKeys: Record<string, string> = {
  'Sync status': 'modules.personaWorkbench.runtimeEvidence.rows.syncStatus',
  'Runtime loaded': 'modules.personaWorkbench.runtimeEvidence.rows.runtimeLoaded',
  'Live applied': 'modules.personaWorkbench.runtimeEvidence.rows.liveApplied',
  'Restart required': 'modules.personaWorkbench.runtimeEvidence.rows.restartRequired',
  'Restart fields': 'modules.personaWorkbench.runtimeEvidence.rows.restartFields',
  'Runtime effect': 'modules.personaWorkbench.runtimeEvidence.rows.runtimeEffect',
  Divergences: 'modules.personaWorkbench.runtimeEvidence.rows.divergences',
};

export function PersonaWorkbenchPage({ personaId, initialTab }: { personaId: string; initialTab?: string | null }) {
  const { t } = useI18n();
  const api = useMemo(() => new ManagementApi(), []);
  const [activeTab, setActiveTab] = useState<TabId>(() => normalizeTab(initialTab));
  const [drawer, setDrawer] = useState<DrawerId>(null);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [structuredDraftDirty, setStructuredDraftDirty] = useState(false);
  const [state, setState] = useState<LoadState>(() => {
    const sources = { personaId };
    return { loading: true, sources, model: buildPersonaWorkbenchModel(sources), errors: {}, actionMessage: '' };
  });

  const load = useCallback(async () => {
    const errors: Record<string, string> = {};
    const capture = async (key: string, promise: Promise<unknown>) => {
      try {
        return await promise;
      } catch (error) {
        errors[key] = error instanceof Error ? error.message : String(error);
        return undefined;
      }
    };

    setConfirmAction(null);
    setState(previous => ({ ...previous, loading: true }));

    const [persona, config, status, promptAssignments, sessions, diff, snapshot, versions, preview] = await Promise.all([
      capture('persona detail', api.getPersona(personaId)),
      capture('persona config', api.getPersonaConfig(personaId)),
      capture('config status', api.getPersonaConfigStatus(personaId)),
      capture('prompt assignments', api.getPromptModuleAssignments(personaId, selectedPlatform)),
      capture('sessions', api.getSessions(personaId)),
      capture('config diff', api.getPersonaConfigDiff(personaId)),
      capture('runtime snapshot', api.getPersonaConfigSnapshot(personaId)),
      capture('versions', api.getPersonaConfigVersions(personaId)),
      capture('config preview', api.getPersonaConfigPreview(personaId)),
    ]);

    const firstSessionId = firstSessionID(sessions);
    const selectedSession = firstSessionId ? await capture('session detail', api.getSession(firstSessionId)) : undefined;
    const configId = configIDFrom(persona, config);
    const audit = configId ? await capture('history', api.getAudit({ resource_id: configId, limit: '20' })) : undefined;

    const sources: PersonaWorkbenchSources = {
      personaId,
      persona,
      config,
      status,
      preview,
      promptAssignments,
      sessions,
      selectedSession,
      diff,
      snapshot,
      versions,
      audit,
      errors,
    };
    setState(previous => ({
      loading: false,
      sources,
      model: buildPersonaWorkbenchModel(sources),
      errors,
      actionMessage: previous.actionMessage,
    }));
  }, [api, personaId, selectedPlatform]);

  useEffect(() => {
    setActiveTab(normalizeTab(initialTab));
  }, [initialTab, personaId]);

  useEffect(() => {
    setConfirmAction(null);
    setStructuredDraftDirty(false);
  }, [personaId]);

  useEffect(() => {
    void load();
  }, [load]);

  const dirtyGuard = useDirtyDraftGuard({ dirty: structuredDraftDirty, message: defaultDirtyDraftGuardMessage });
  const guardedSetDrawer = (nextDrawer: DrawerId) => {
    if (nextDrawer === null && drawer !== null) {
      dirtyGuard.guardDrawerClose(() => setDrawer(null));
      return;
    }
    setDrawer(nextDrawer);
  };

  const selectTab = (tab: string) => {
    const next = normalizeTab(tab);
    if (next === activeTab) return;
    dirtyGuard.guardTabSwitch(next, selectedTab => {
      setActiveTab(normalizeTab(selectedTab));
      if (typeof window !== 'undefined') {
        window.history.replaceState(null, '', `#/personas/${encodeURIComponent(personaId)}/workbench?tab=${next}`);
      }
    });
  };

  const openSessionDetail = async (sessionId: string) => {
    if (!sessionId) return;
    setDrawer('session');
    try {
      const selectedSession = await api.getSession(sessionId);
      setState(previous => {
        const sources = { ...previous.sources, selectedSession };
        const errors = { ...previous.errors };
        delete errors['session detail'];
        return { ...previous, sources, errors, model: buildPersonaWorkbenchModel(sources) };
      });
    } catch (error) {
      setState(previous => ({
        ...previous,
        errors: { ...previous.errors, 'session detail': error instanceof Error ? error.message : String(error) },
      }));
    }
  };

  const saveStructuredDraft = async (values: PersonaConfigSpec, baseRevision: number) => {
    const savedDraft = await api.savePersonaConfigDraft(personaId, {
      spec: compactPersonaConfigSpec(values),
      base_revision: baseRevision,
      actor: 'console',
    });
    const sources: PersonaWorkbenchSources = {
      ...state.sources,
      config: {
        ...asRecord(state.sources.config),
        draft: savedDraft,
      },
    };
    setConfirmAction(null);
    setState(previous => ({
      ...previous,
      sources,
      model: buildPersonaWorkbenchModel(sources),
      actionMessage: t('modules.personaWorkbench.actions.messages.draftSaved'),
    }));
    await load();
    return compactPersonaConfigSpec(values);
  };

  const runAction = async (actionKey: RunnableAction, options: ActionRunOptions = {}) => {
    const action = state.model.actions.find(item => item.key === actionKey);
    if (!action) {
      setState(previous => ({ ...previous, actionMessage: t('modules.personaWorkbench.actions.messages.noCurrentContract') }));
      return;
    }
    const actionForRun = actionKey === 'publish' && options.targetVersion
      ? {
          ...action,
          targetVersion: options.targetVersion,
          confirmationKey: `${personaId}:publish:v${options.targetVersion}`,
          copyValues: { ...(action.copyValues ?? {}), targetVersion: options.targetVersion },
        }
      : action;
    if (!canRunPersonaWorkbenchAction(actionForRun, confirmAction, state.loading)) {
      const reason = state.loading
        ? t('modules.personaWorkbench.actions.messages.loading')
        : actionPreview(actionForRun, t) || t('modules.personaWorkbench.actions.messages.noConfirmationContract');
      setState(previous => ({ ...previous, actionMessage: reason }));
      return;
    }

    setState(previous => ({ ...previous, actionMessage: t('modules.personaWorkbench.actions.messages.applying', { action: actionLabel(actionForRun, t) }) }));
    try {
      if (actionKey === 'validate') {
        const validation = await api.validatePersonaConfig(personaId);
        setState(previous => {
          const sources = { ...previous.sources, validation };
          const errors = { ...previous.errors };
          delete errors['config validate'];
          return {
            ...previous,
            sources,
            errors,
            model: buildPersonaWorkbenchModel(sources),
            actionMessage: t('modules.personaWorkbench.actions.messages.validationCompleted'),
          };
        });
        setDrawer('validation');
        return;
      }

      if (actionKey === 'preview') {
        const preview = await api.getPersonaConfigPreview(personaId);
        setState(previous => {
          const sources = { ...previous.sources, preview };
          const errors = { ...previous.errors };
          delete errors['config preview'];
          return {
            ...previous,
            sources,
            errors,
            model: buildPersonaWorkbenchModel(sources),
            actionMessage: t('modules.personaWorkbench.actions.messages.previewRefreshed'),
          };
        });
        setDrawer('preview');
        return;
      }

      if (actionKey === 'create_version') {
        await api.createPersonaConfigVersion(personaId, t('modules.personaWorkbench.actions.messages.versionChangelog'));
      } else if (actionKey === 'publish') {
        if (!actionForRun.targetVersion) throw new Error(t('modules.personaWorkbench.actions.messages.noPublishableVersion'));
        await api.publishPersonaConfig(personaId, actionForRun.targetVersion);
      }

      setConfirmAction(null);
      setState(previous => ({ ...previous, actionMessage: t('modules.personaWorkbench.actions.messages.applied', { action: actionLabel(actionForRun, t) }) }));
      await load();
    } catch (error) {
      setState(previous => ({ ...previous, actionMessage: error instanceof Error ? error.message : String(error) }));
    }
  };

  const { model } = state;
  const draft = asRecord(asRecord(state.sources.config).draft);
  const manifest = asRecord(asRecord(state.sources.config).manifest);
  const selectedSession = state.sources.selectedSession;

  return (
    <div className="page-stack">
      <ResourceHeader
        eyebrow="Persona resource"
        title={model.header.title}
        description={model.header.description}
        meta={model.header.meta}
        actions={
          <PersonaHeaderActions
            actions={model.actions}
            loading={state.loading}
            onRefresh={load}
            onRun={runAction}
            openDrawer={setDrawer}
          />
        }
      />

      <StatusStrip items={model.status} />
      <UnavailableNotice errors={state.errors} loading={state.loading} />
      {state.actionMessage ? <div className="notice"><strong>{t('modules.personaWorkbench.actions.resultLabel')}</strong> {state.actionMessage}</div> : null}

      <Tabs tabs={tabs} activeId={activeTab} onSelect={selectTab} />

      {activeTab === 'overview' ? (
        <OverviewTab model={model} sources={state.sources} openDrawer={setDrawer} />
      ) : null}
      {activeTab === 'configuration' ? (
        <ConfigurationTab
          model={model}
          draft={draft}
          manifest={manifest}
          sources={state.sources}
          selectedPlatform={selectedPlatform}
          onPlatformChange={setSelectedPlatform}
          openDrawer={guardedSetDrawer}
          onSaveStructuredDraft={saveStructuredDraft}
          onStructuredDirtyChange={setStructuredDraftDirty}
        />
      ) : null}
      {activeTab === 'runtime' ? (
        <RuntimeTab model={model} selectedSession={selectedSession} onOpenSession={openSessionDetail} openDrawer={setDrawer} />
      ) : null}
      {activeTab === 'history' ? (
        <HistoryTab sources={state.sources} openDrawer={setDrawer} />
      ) : null}

      <WorkbenchDrawer drawer={drawer} setDrawer={guardedSetDrawer} state={state} selectedPlatform={selectedPlatform} selectedSession={selectedSession} confirmAction={confirmAction} onConfirm={setConfirmAction} onRun={runAction} />
    </div>
  );
}

function OverviewTab({
  model,
  sources,
  openDrawer,
}: {
  model: PersonaWorkbenchModel;
  sources: PersonaWorkbenchSources;
  openDrawer: (drawer: DrawerId) => void;
}) {
  const { t } = useI18n();
  const runtime = asRecord(asRecord(sources.persona).runtime);
  return (
    <div className="content-grid">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Resource summary</h2>
            <p>Live Persona metadata from the management API.</p>
          </div>
          <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('config')}>{t('modules.personaWorkbench.configCenter.draftJsonDiffAction')}</button></div>
        </div>
        <PropertyTable rows={[
          ['Persona', model.header.title],
          ['Config ID', model.configId || 'Unavailable'],
          ['Work dir', textOrUnavailable(asRecord(sources.persona).work_dir)],
          ['Real platforms', model.platforms.length ? model.platforms.join(', ') : 'Unavailable'],
          ['Provider', textOrUnavailable(runtime.active_provider)],
          ['Model', textOrUnavailable(runtime.current_model)],
        ]} />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Prompt assignment summary</h2>
            <p>Effective module order and assignment state.</p>
          </div>
          <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('prompt')}>{t('modules.personaWorkbench.actions.promptAssignments')}</button></div>
        </div>
        <PropertyTable rows={[
          ['Enabled modules', `${model.promptAssignments.enabledCount}/${model.promptAssignments.totalCount}`],
          ['Draft revision', model.promptAssignments.draftRevision || 'Unavailable'],
          ['Draft changed', model.promptAssignments.draftChanged ? 'Yes' : 'No'],
          ['Module source', model.promptAssignments.moduleSource],
          ['Injection order', model.promptAssignments.injectionOrder.join(' → ') || 'Unavailable'],
        ]} />
      </section>
    </div>
  );
}

function ConfigurationTab({
  model,
  draft,
  manifest,
  sources,
  selectedPlatform,
  onPlatformChange,
  openDrawer,
  onSaveStructuredDraft,
  onStructuredDirtyChange,
}: {
  model: PersonaWorkbenchModel;
  draft: Record<string, unknown>;
  manifest: Record<string, unknown>;
  sources: PersonaWorkbenchSources;
  selectedPlatform: string;
  onPlatformChange: (platform: string) => void;
  openDrawer: (drawer: DrawerId) => void;
  onSaveStructuredDraft: (values: PersonaConfigSpec, baseRevision: number) => Promise<PersonaConfigSpec | void>;
  onStructuredDirtyChange: (dirty: boolean) => void;
}) {
  const { t } = useI18n();
  const promptScopeOptions = scopeOptions(model.platforms, selectedPlatform);
  return (
    <div className="page-stack">
      <PersonaStructuredConfigEditor
        sources={sources}
        onSaveDraft={onSaveStructuredDraft}
        onDirtyChange={onStructuredDirtyChange}
      />

      <PersonaConfigCenterView
        model={model}
        selectedPlatform={selectedPlatform}
        onPlatformChange={onPlatformChange}
        openDrawer={openDrawer}
      />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{t('modules.personaWorkbench.configCenter.draftLifecycleTitle')}</h2>
            <p>{t('modules.personaWorkbench.configCenter.draftLifecycleDescription')}</p>
          </div>
          <div className="action-row">
            <button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('config')}>{t('modules.personaWorkbench.configCenter.draftJsonDiffAction')}</button>
            <button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('contracts')}>{t('modules.personaWorkbench.actions.contractsDrawer')}</button>
          </div>
        </div>
        <PropertyTable rows={[
          ['Manifest ID', textOrUnavailable(manifest.id)],
          ['Published version', versionText(manifest.published_version)],
          ['Draft revision', textOrUnavailable(draft.revision)],
          ['Updated by', textOrUnavailable(draft.updated_by)],
          ['Prompt source', model.promptAssignments.moduleSource],
        ]} />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Prompt modules</h2>
            <p>Built-in and managed assignment detail for the selected platform scope.</p>
          </div>
          <div className="action-row">
            <span className="pill pill-blue">Active scope: {formatScopeLabel(selectedPlatform)}</span>
            <label className="field-label">
              {t('modules.personaWorkbench.configCenter.promptPlatform')}
              <select value={selectedPlatform} onChange={event => onPlatformChange(event.target.value)}>
                <option value="">{t('modules.personaWorkbench.configCenter.defaultPersona')}</option>
                {promptScopeOptions.map(platform => <option key={platform} value={platform}>{platform}</option>)}
              </select>
            </label>
            <button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('prompt')}>{t('modules.personaWorkbench.actions.promptAssignments')}</button>
          </div>
        </div>
        <PromptModuleTable modules={model.promptAssignments.modules} selectedPlatform={selectedPlatform} moduleSource={model.promptAssignments.moduleSource} />
      </section>
    </div>
  );
}

export function PersonaConfigCenterView({
  model,
  selectedPlatform,
  onPlatformChange,
  openDrawer,
}: {
  model: PersonaWorkbenchModel;
  selectedPlatform: string;
  onPlatformChange: (platform: string) => void;
  openDrawer: (drawer: DrawerId) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="page-stack">
      <div className="content-grid">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{t('modules.personaWorkbench.configCenter.runtimeTitle')}</h2>
              <p>{t('modules.personaWorkbench.configCenter.runtimeDescription')}</p>
            </div>
          </div>
          <PropertyTable rows={translateRows(model.configCenter.runtimeRows, t, configCenterRowKeys)} />
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{t('modules.personaWorkbench.configCenter.consoleTitle')}</h2>
              <p>{t('modules.personaWorkbench.configCenter.consoleDescription')}</p>
            </div>
          </div>
          <PropertyTable rows={translateRows(model.configCenter.consoleRows, t, configCenterRowKeys)} />
        </section>
      </div>

      <div className="content-grid">
        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{t('modules.personaWorkbench.configCenter.bindingsTitle')}</h2>
              <p>{t('modules.personaWorkbench.configCenter.bindingsDescription')}</p>
            </div>
            <div className="action-row">
              <a className="btn btn-compact btn-subtle" href={`#/personas/${encodeURIComponent(model.personaId)}/prompts`}>{t('modules.personaWorkbench.configCenter.managePromptBindings')}</a>
              <button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('prompt')}>{t('modules.personaWorkbench.configCenter.promptEvidence')}</button>
            </div>
          </div>
          <PropertyTable rows={translateRows(model.configCenter.bindingRows, t, configCenterRowKeys)} />
        </section>

        <section className="panel">
          <div className="panel-header">
            <div>
              <h2>{t('modules.personaWorkbench.configCenter.hierarchyTitle')}</h2>
              <p>{t('modules.personaWorkbench.configCenter.hierarchyDescription')}</p>
            </div>
            <div className="action-row">
              <label className="field-label">
                {t('modules.personaWorkbench.configCenter.promptPlatform')}
                <select value={selectedPlatform} onChange={event => onPlatformChange(event.target.value)}>
                  <option value="">{t('modules.personaWorkbench.configCenter.defaultPersona')}</option>
                  {model.platforms.map(platform => <option key={platform} value={platform}>{platform}</option>)}
                </select>
              </label>
              <button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('config')}>{t('modules.personaWorkbench.configCenter.draftJsonDiffAction')}</button>
            </div>
          </div>
          <PropertyTable rows={compactRows(translateRows(model.configCenter.layerRows, t, configCenterRowKeys), t('modules.personaWorkbench.configCenter.moreRowsSummary'))} />
        </section>
      </div>
    </div>
  );
}

function RuntimeTab({
  model,
  selectedSession,
  onOpenSession,
  openDrawer,
}: {
  model: PersonaWorkbenchModel;
  selectedSession: unknown;
  onOpenSession: (sessionId: string) => void;
  openDrawer: (drawer: DrawerId) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="page-stack">
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Runtime sessions</h2>
            <p>Session, RuntimeProfile, Codex thread, and history evidence.</p>
          </div>
          <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('session')}>{t('modules.personaWorkbench.runtimeEvidence.sessionDetail')}</button></div>
        </div>
        {model.sessions.length ? (
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Platform</th><th>Live</th><th>RuntimeProfile</th><th>Thread</th><th>History</th></tr>
            </thead>
            <tbody>
              {model.sessions.map(session => (
                <tr key={session.id} onClick={() => void onOpenSession(session.id)}>
                  <td>{session.name}</td>
                  <td><span className="badge">{session.platform}</span></td>
                  <td>{session.live ? 'Yes' : 'No'}</td>
                  <td>{session.runtimeProfileId || 'Unavailable'}</td>
                  <td>{session.codexThreadId || 'Unavailable'}</td>
                  <td>{session.historyCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <EmptyState title="No sessions" description="The management API returned no sessions for this persona." />}
      </section>

      <RuntimeDivergenceEvidenceView model={model} openDrawer={openDrawer} />

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>Selected session evidence</h2>
            <p>RuntimeProfile, thread, prompt, and history summary. Full JSON stays behind the detail drawer.</p>
          </div>
          <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('session')}>{t('modules.personaWorkbench.runtimeEvidence.sessionJson')}</button></div>
        </div>
        <SessionEvidenceSummary selectedSession={selectedSession} />
      </section>
    </div>
  );
}

export function RuntimeDivergenceEvidenceView({
  model,
  openDrawer,
}: {
  model: PersonaWorkbenchModel;
  openDrawer: (drawer: DrawerId) => void;
}) {
  const { t } = useI18n();
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>{t('modules.personaWorkbench.runtimeEvidence.title')}</h2>
          <p>{t('modules.personaWorkbench.runtimeEvidence.description')}</p>
        </div>
        <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('snapshot')}>{t('modules.personaWorkbench.runtimeEvidence.snapshotDetail')}</button></div>
      </div>
      <PropertyTable rows={translateRows(model.runtimeEvidenceRows, t, runtimeEvidenceRowKeys)} />
    </section>
  );
}

function SessionEvidenceSummary({ selectedSession }: { selectedSession: unknown }) {
  if (selectedSession === undefined || selectedSession === null) {
    return <EmptyState title="No selected session" description="Select a runtime session to load detail, prompt modules, and history." />;
  }
  const session = asRecord(selectedSession);
  const profile = asRecord(session.runtime_profile);
  const promptModules = getArray(session.prompt_modules).map(module => asRecord(module));
  const history = getArray(session.history);
  const promptNames = promptModules
    .slice(0, 8)
    .map(module => text(module.display_name) || formatModuleName(text(module.name)))
    .filter(Boolean)
    .join(', ');
  return (
    <PropertyTable rows={[
      ['Session ID', textOrUnavailable(session.id)],
      ['Platform', textOrUnavailable(session.platform)],
      ['RuntimeProfile', textOrUnavailable(profile.id)],
      ['Codex thread', textOrUnavailable(profile.codex_thread_id)],
      ['Agent session', textOrUnavailable(session.agent_session_id)],
      ['Prompt modules', promptModules.length ? `${promptModules.length} (${promptNames || 'names unavailable'})` : 'Unavailable'],
      ['History entries', String(history.length)],
    ]} />
  );
}

function HistoryTab({ sources, openDrawer }: { sources: PersonaWorkbenchSources; openDrawer: (drawer: DrawerId) => void }) {
  const { t } = useI18n();
  const versions = getArray(asRecord(sources.versions).versions);
  const auditEvents = getArray(asRecord(sources.audit).events);
  return (
    <div className="content-grid">
      <section className="panel">
        <div className="panel-header">
          <div><h2>Versions</h2><p>Published Persona config history.</p></div>
          <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('snapshot')}>{t('modules.personaWorkbench.runtimeEvidence.snapshotDetail')}</button></div>
        </div>
        {versions.length ? <RecordList records={versions} titleKey="version" /> : <EmptyState title="No versions" description="No published config versions were returned." />}
      </section>
      <section className="panel">
        <div className="panel-header">
          <div><h2>Audit history</h2><p>Control-plane events scoped to this Persona config.</p></div>
          <div className="action-row"><button className="btn btn-compact btn-subtle" type="button" onClick={() => openDrawer('config')}>{t('modules.personaWorkbench.configCenter.diffView')}</button></div>
        </div>
        {auditEvents.length ? <RecordList records={auditEvents} titleKey="action" /> : <EmptyState title="No audit events" description="Audit API returned an empty history for this resource." />}
      </section>
    </div>
  );
}

export function WorkbenchDrawer({
  drawer,
  setDrawer,
  state,
  selectedPlatform = '',
  selectedSession,
  confirmAction = null,
  onConfirm = () => undefined,
  onRun = () => undefined,
}: {
  drawer: DrawerId;
  setDrawer: (drawer: DrawerId) => void;
  state: LoadState;
  selectedPlatform?: string;
  selectedSession: unknown;
  confirmAction?: ConfirmAction;
  onConfirm?: (action: ConfirmAction) => void;
  onRun?: (action: RunnableAction, options?: ActionRunOptions) => void;
}) {
  const { t } = useI18n();
  const close = () => setDrawer(null);
  const [visibleVersionCount, setVisibleVersionCount] = useState(10);
  const [selectedPublishVersion, setSelectedPublishVersion] = useState<number | null>(null);
  useEffect(() => {
    if (drawer === 'publish') {
      setVisibleVersionCount(10);
      setSelectedPublishVersion(null);
    }
  }, [drawer]);
  return (
    <>
      <Drawer title="Prompt assignment detail" open={drawer === 'prompt'} onClose={close}>
        <PromptModuleTable modules={state.model.promptAssignments.modules} selectedPlatform={selectedPlatform} moduleSource={state.model.promptAssignments.moduleSource} />
      </Drawer>
      <Drawer title="View Config JSON" open={drawer === 'json'} onClose={close} badge="redacted">
        <pre className="json-preview">{state.model.configCenter.configJson}</pre>
      </Drawer>
      <Drawer title="Runtime snapshot" open={drawer === 'snapshot'} onClose={close} badge={state.errors['runtime snapshot'] ? 'unavailable' : 'redacted'}>
        <JsonBlock value={state.sources.snapshot} empty={state.errors['runtime snapshot'] || 'No published snapshot returned.'} />
      </Drawer>
      <Drawer title="Session detail" open={drawer === 'session'} onClose={close} badge={state.errors['session detail'] ? 'unavailable' : 'redacted'}>
        <JsonBlock value={selectedSession} empty={state.errors['session detail'] || 'No session detail loaded.'} />
      </Drawer>
      <Drawer title={t('modules.personaWorkbench.drawers.draftJsonTitle')} open={drawer === 'config'} onClose={close} badge={state.errors['persona config'] || state.errors['config diff'] ? 'unavailable' : 'redacted'}>
        <article className="summary-card">
          <h3>{t('modules.personaWorkbench.drawers.draftJsonSectionTitle')}</h3>
          {state.model.configCenter.draftJson && state.model.configCenter.draftJson !== '{}'
            ? <pre className="json-preview">{state.model.configCenter.draftJson}</pre>
            : <EmptyState title={t('states.unavailable')} description={state.errors['persona config'] || t('modules.personaWorkbench.drawers.noDraftPayload')} />}
        </article>
        <article className="summary-card">
          <h3>{t('modules.personaWorkbench.drawers.draftDiffTitle')}</h3>
          <JsonBlock value={state.sources.diff} empty={state.errors['config diff'] || t('modules.personaWorkbench.drawers.noDiffPayload')} redactPromptContent />
        </article>
      </Drawer>
      <Drawer title={t('modules.personaWorkbench.drawers.previewTitle')} open={drawer === 'preview'} onClose={close} badge={state.errors['config preview'] ? 'unavailable' : 'redacted'}>
        <JsonBlock value={state.sources.preview} empty={state.errors['config preview'] || t('modules.personaWorkbench.drawers.noPreviewPayload')} />
      </Drawer>
      <Drawer title={t('modules.personaWorkbench.drawers.validationTitle')} open={drawer === 'validation'} onClose={close} badge={state.errors['config validate'] ? 'unavailable' : 'redacted'}>
        <ValidateDraftDrawerContent state={state} confirmAction={confirmAction} onConfirm={onConfirm} onRun={onRun} onCancel={close} />
      </Drawer>
      <Drawer title={t('modules.personaWorkbench.drawers.savePublishTitle')} open={drawer === 'publish'} onClose={close} badge={state.errors['versions'] ? 'unavailable' : 'guarded'}>
        <SavePublishDrawerContent
          state={state}
          confirmAction={confirmAction}
          visibleVersionCount={visibleVersionCount}
          selectedPublishVersion={selectedPublishVersion}
          onConfirm={onConfirm}
          onRun={onRun}
          onSelectVersion={setSelectedPublishVersion}
          onMore={() => setVisibleVersionCount(count => count + 10)}
          onCancel={close}
        />
      </Drawer>
      <Drawer title={t('modules.personaWorkbench.actions.matrix.title')} open={drawer === 'contracts'} onClose={close} badge={t('modules.personaWorkbench.actions.contractsBadge')}>
        <ActionContractTable actions={state.model.actions} confirmAction={confirmAction} loading={state.loading} onConfirm={onConfirm} onRun={onRun} />
      </Drawer>
    </>
  );
}

function ValidateDraftDrawerContent({
  state,
  confirmAction,
  onConfirm,
  onRun,
  onCancel,
}: {
  state: LoadState;
  confirmAction: ConfirmAction;
  onConfirm: (action: ConfirmAction) => void;
  onRun: (action: RunnableAction, options?: ActionRunOptions) => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const createVersionAction = state.model.actions.find(action => action.key === 'create_version');
  const saveConfirmed = Boolean(
    createVersionAction?.confirmationKey
    && confirmAction?.key === 'create_version'
    && confirmAction.confirmationKey === createVersionAction.confirmationKey,
  );
  const canSaveDraft = canRunPersonaWorkbenchAction(createVersionAction, confirmAction, state.loading);
  return (
    <div className="page-stack">
      <article className="summary-card">
        <h3>{t('modules.personaWorkbench.drawers.validationResultTitle')}</h3>
        <JsonBlock value={state.sources.validation} empty={state.errors['config validate'] || t('modules.personaWorkbench.drawers.noValidationPayload')} />
      </article>
      <article className="summary-card">
        <h3>{t('modules.personaWorkbench.drawers.draftDiffTitle')}</h3>
        <JsonBlock value={state.sources.diff} empty={state.errors['config diff'] || t('modules.personaWorkbench.drawers.noDiffPayload')} redactPromptContent />
      </article>
      {createVersionAction ? (
        <label className="confirm-row">
          <input
            type="checkbox"
            checked={saveConfirmed}
            disabled={state.loading || !createVersionAction.available || !createVersionAction.confirmationKey}
            onChange={event => onConfirm(event.target.checked && createVersionAction.confirmationKey
              ? { key: 'create_version', confirmationKey: createVersionAction.confirmationKey }
              : null)}
          />
          {t('modules.personaWorkbench.actions.confirm.reviewedEvidence')}
        </label>
      ) : null}
      {createVersionAction && !createVersionAction.available ? <p className="muted-inline">{actionPreview(createVersionAction, t)}</p> : null}
      <div className="drawer-actions">
        <button className="btn btn-primary" type="button" disabled={!canSaveDraft} onClick={() => onRun('create_version')}>{t('modules.personaWorkbench.actions.saveDraft')}</button>
        <button className="btn btn-subtle" type="button" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}

function SavePublishDrawerContent({
  state,
  confirmAction,
  visibleVersionCount,
  selectedPublishVersion,
  onConfirm,
  onRun,
  onSelectVersion,
  onMore,
  onCancel,
}: {
  state: LoadState;
  confirmAction: ConfirmAction;
  visibleVersionCount: number;
  selectedPublishVersion: number | null;
  onConfirm: (action: ConfirmAction) => void;
  onRun: (action: RunnableAction, options?: ActionRunOptions) => void;
  onSelectVersion: (version: number | null) => void;
  onMore: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const versions = personaConfigVersionSummaries(state.sources);
  const visibleVersions = versions.slice(0, visibleVersionCount);
  const effectiveSelectedVersion = versions.some(version => version.version === selectedPublishVersion)
    ? selectedPublishVersion
    : versions[0]?.version ?? null;
  const createVersionAction = state.model.actions.find(action => action.key === 'create_version');
  const publishAction = state.model.actions.find(action => action.key === 'publish');
  const publishActionForSelection: PersonaWorkbenchAction | undefined = publishAction && effectiveSelectedVersion
    ? {
        ...publishAction,
        targetVersion: effectiveSelectedVersion,
        confirmationKey: `${state.model.personaId}:publish:v${effectiveSelectedVersion}`,
        copyValues: { ...(publishAction.copyValues ?? {}), targetVersion: effectiveSelectedVersion },
      }
    : publishAction;
  const saveConfirmed = Boolean(
    createVersionAction?.confirmationKey
    && confirmAction?.key === 'create_version'
    && confirmAction.confirmationKey === createVersionAction.confirmationKey,
  );
  const publishConfirmed = Boolean(
    publishActionForSelection?.confirmationKey
    && confirmAction?.key === 'publish'
    && confirmAction.confirmationKey === publishActionForSelection.confirmationKey,
  );
  const canSaveDraft = canRunPersonaWorkbenchAction(createVersionAction, confirmAction, state.loading);
  const canPublish = Boolean(effectiveSelectedVersion && canRunPersonaWorkbenchAction(publishActionForSelection, confirmAction, state.loading));
  const draftChanged = draftHasChanges(state.sources);

  return (
    <div className="page-stack">
      {draftChanged ? (
        <section className="notice notice-warning">
          <strong>{t('modules.personaWorkbench.drawers.unsavedDraftTitle')}</strong>
          <p>{t('modules.personaWorkbench.drawers.unsavedDraftDescription')}</p>
          <PropertyTable rows={draftLifecycleRows(state.sources, t)} />
          <label className="confirm-row">
            <input
              type="checkbox"
              checked={saveConfirmed}
              disabled={state.loading || !createVersionAction?.available || !createVersionAction.confirmationKey}
              onChange={event => onConfirm(event.target.checked && createVersionAction?.confirmationKey
                ? { key: 'create_version', confirmationKey: createVersionAction.confirmationKey }
                : null)}
            />
            {t('modules.personaWorkbench.actions.confirm.reviewedEvidence')}
          </label>
          {createVersionAction && !createVersionAction.available ? <p className="muted-inline">{actionPreview(createVersionAction, t)}</p> : null}
          <div className="drawer-actions">
            <button className="btn btn-primary" type="button" disabled={!canSaveDraft} onClick={() => onRun('create_version')}>{t('modules.personaWorkbench.actions.saveDraft')}</button>
          </div>
        </section>
      ) : (
        <section className="notice notice-info">
          <strong>{t('modules.personaWorkbench.drawers.savedVersionTitle')}</strong>
          <p>{t('modules.personaWorkbench.drawers.savedVersionDescription')}</p>
        </section>
      )}

      <article className="summary-card">
        <h3>{t('modules.personaWorkbench.drawers.draftDiffTitle')}</h3>
        <JsonBlock value={state.sources.diff} empty={state.errors['config diff'] || t('modules.personaWorkbench.drawers.noDiffPayload')} redactPromptContent />
      </article>

      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>{t('modules.personaWorkbench.drawers.publishVersionTitle')}</h2>
            <p>{t('modules.personaWorkbench.drawers.publishVersionDescription')}</p>
          </div>
        </div>
        {visibleVersions.length ? (
          <div className="record-list">
            {visibleVersions.map(version => (
              <label className="record-card confirm-row version-choice" key={version.version}>
                <input
                  type="radio"
                  name="persona-publish-version"
                  checked={effectiveSelectedVersion === version.version}
                  onChange={() => {
                    onSelectVersion(version.version);
                    onConfirm(null);
                  }}
                />
                <span>
                  <strong>{version.title}</strong>
                  <span className="muted-inline">{version.summary}</span>
                  <small>{version.meta.join(' · ')}</small>
                </span>
              </label>
            ))}
          </div>
        ) : <EmptyState title={t('modules.personaWorkbench.drawers.noVersionsTitle')} description={state.errors.versions || t('modules.personaWorkbench.drawers.noVersionsDescription')} />}
        {versions.length > visibleVersionCount ? (
          <div className="drawer-actions">
            <button className="btn btn-subtle" type="button" onClick={onMore}>{t('modules.personaWorkbench.actions.moreVersions')}</button>
          </div>
        ) : null}
      </section>

      <article className="summary-card">
        <h3>{t('modules.personaWorkbench.drawers.previewTitle')}</h3>
        <JsonBlock value={state.sources.preview} empty={state.errors['config preview'] || t('modules.personaWorkbench.drawers.noPreviewPayload')} />
      </article>

      {publishActionForSelection ? (
        <label className="confirm-row">
          <input
            type="checkbox"
            checked={publishConfirmed}
            disabled={state.loading || !effectiveSelectedVersion || !publishActionForSelection.available || !publishActionForSelection.confirmationKey}
            onChange={event => onConfirm(event.target.checked && publishActionForSelection.confirmationKey
              ? { key: 'publish', confirmationKey: publishActionForSelection.confirmationKey }
              : null)}
          />
          {t('modules.personaWorkbench.actions.confirm.reviewPublishEvidence')}
        </label>
      ) : null}
      {publishActionForSelection && !publishActionForSelection.available ? <p className="muted-inline">{actionPreview(publishActionForSelection, t)}</p> : null}
      <div className="drawer-actions">
        <button className="btn btn-primary" type="button" disabled={!canPublish} onClick={() => effectiveSelectedVersion ? onRun('publish', { targetVersion: effectiveSelectedVersion }) : undefined}>{t('modules.personaWorkbench.actions.publishSelected')}</button>
        <button className="btn btn-subtle" type="button" onClick={onCancel}>{t('common.cancel')}</button>
      </div>
    </div>
  );
}


function isRunnableAction(key: PersonaWorkbenchActionKey): key is RunnableAction {
  return key === 'validate' || key === 'preview' || key === 'create_version' || key === 'publish';
}

function isConfirmableAction(key: PersonaWorkbenchActionKey): key is PersonaWorkbenchConfirmation['key'] {
  return key === 'create_version' || key === 'publish';
}

function actionLabel(action: PersonaWorkbenchAction, t: Translate) {
  return t(action.labelKey, action.copyValues) || action.label;
}

function actionPreview(action: PersonaWorkbenchAction, t: Translate) {
  return t(action.previewKey, action.copyValues) || action.preview;
}

function actionConfirmation(action: PersonaWorkbenchAction, t: Translate) {
  return t(action.confirmationTextKey, action.copyValues) || action.confirmation;
}

function actionBackend(action: PersonaWorkbenchAction, t: Translate) {
  return action.backendKey ? t(action.backendKey, action.copyValues) : action.backend;
}

function translateRows(rows: Array<[string, string]>, t: Translate, keys: Record<string, string>): Array<[string, string]> {
  return rows.map(([label, value]) => [keys[label] ? t(keys[label]) : label, value]);
}

function compactRows(rows: Array<[string, string]>, moreLabel: string): Array<[string, string]> {
  if (rows.length <= 3) return rows;
  return [...rows.slice(0, 3), [moreLabel, String(rows.length - 3)]];
}

function PersonaHeaderActions({
  actions,
  loading,
  onRefresh,
  onRun,
  openDrawer,
}: {
  actions: PersonaWorkbenchAction[];
  loading: boolean;
  onRefresh: () => Promise<void>;
  onRun: (action: RunnableAction, options?: ActionRunOptions) => void;
  openDrawer: (drawer: DrawerId) => void;
}) {
  const { t } = useI18n();
  const validateAction = actions.find((action): action is PersonaWorkbenchAction & { key: RunnableAction } => action.key === 'validate');
  return (
    <div className="action-row">
      <button className="btn btn-subtle" type="button" onClick={() => openDrawer('json')}>{t('modules.personaWorkbench.actions.rawJson')}</button>
      {validateAction ? (
        <button
          className="btn btn-subtle"
          type="button"
          disabled={!canRunPersonaWorkbenchAction(validateAction, null, loading)}
          title={validateAction.available ? actionPreview(validateAction, t) : actionConfirmation(validateAction, t)}
          onClick={() => {
            openDrawer('validation');
            onRun(validateAction.key);
          }}
        >
          {actionLabel(validateAction, t)}
        </button>
      ) : null}
      <button className="btn btn-primary" type="button" onClick={() => openDrawer('publish')}>{t('modules.personaWorkbench.actions.savePublish')}</button>
      <RefreshActionButton
        label={t('modules.personaWorkbench.actions.refreshLiveData')}
        busyLabel={t('modules.personaWorkbench.actions.refreshing')}
        loading={loading}
        onClick={onRefresh}
      />
    </div>
  );
}

function ActionContractTable({
  actions,
  confirmAction,
  loading,
  onConfirm,
  onRun,
}: {
  actions: PersonaWorkbenchAction[];
  confirmAction: ConfirmAction;
  loading: boolean;
  onConfirm: (action: ConfirmAction) => void;
  onRun: (action: RunnableAction, options?: ActionRunOptions) => void;
}) {
  const { t } = useI18n();
  return (
    <section className="panel">
      <div className="panel-header">
        <div>
          <h2>{t('modules.personaWorkbench.actions.matrix.title')}</h2>
          <p>{t('modules.personaWorkbench.actions.matrix.description')}</p>
        </div>
      </div>
      <table className="data-table">
        <thead>
          <tr><th>{t('modules.personaWorkbench.actions.matrix.action')}</th><th>{t('modules.personaWorkbench.actions.matrix.backend')}</th><th>{t('modules.personaWorkbench.actions.matrix.previewState')}</th><th>{t('modules.personaWorkbench.actions.matrix.confirmation')}</th><th>{t('modules.personaWorkbench.actions.matrix.run')}</th></tr>
        </thead>
        <tbody>
          {actions.map(action => {
            const requiresConfirmation = isConfirmableAction(action.key);
            const confirmed = Boolean(confirmAction?.key === action.key && confirmAction.confirmationKey === action.confirmationKey);
            return (
              <tr key={action.key}>
                <td><strong>{actionLabel(action, t)}</strong></td>
                <td>{actionBackend(action, t)}</td>
                <td>{actionPreview(action, t)}</td>
                <td>
                  {requiresConfirmation && action.available ? (
                    <label className="confirm-row">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        disabled={loading || !action.confirmationKey}
                        onChange={event => onConfirm(event.target.checked && isConfirmableAction(action.key) && action.confirmationKey
                          ? { key: action.key, confirmationKey: action.confirmationKey }
                          : null)}
                      />
                      {t('modules.personaWorkbench.actions.confirm.reviewedEvidence')}
                    </label>
                  ) : actionConfirmation(action, t)}
                </td>
                <td>
                  <button
                    className="btn btn-compact"
                    type="button"
                    disabled={!canRunPersonaWorkbenchAction(action, confirmAction, loading)}
                    onClick={() => isRunnableAction(action.key) ? onRun(action.key) : undefined}
                  >
                    {action.available ? t('modules.personaWorkbench.actions.run') : t('modules.personaWorkbench.actions.unavailable')}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

export function PromptModuleTable({ modules, selectedPlatform = '', moduleSource = '' }: { modules: Array<Record<string, unknown>>; selectedPlatform?: string; moduleSource?: string }) {
  if (!modules.length) {
    return <EmptyState title="No prompt modules" description="Prompt assignment API returned no modules or is unavailable." />;
  }
  return (
    <>
      <div className="action-row prompt-module-scope-bar">
        <span className="pill pill-blue">Active scope: {formatScopeLabel(selectedPlatform)}</span>
        {moduleSource ? <span className="pill">Module source: {moduleSource}</span> : null}
      </div>
      <table className="data-table">
        <thead>
          <tr><th>Module</th><th>Origin</th><th>Scope</th><th>Status</th><th>Selected scope</th><th>Source</th><th>Priority</th></tr>
        </thead>
        <tbody>
          {modules.map(module => {
            const selectedEnabled = promptModuleSelectedEnabled(module);
            const sourceDetail = promptModuleSourceDetail(module);
            return (
              <tr key={`${textOrUnavailable(module.name)}-${textOrUnavailable(module.assignment_source)}-${textOrUnavailable(module.selected_scope)}`}>
                <td>{textOrUnavailable(module.display_name) !== 'Unavailable' ? textOrUnavailable(module.display_name) : textOrUnavailable(module.name)}</td>
                <td>{textOrUnavailable(module.origin)}</td>
                <td>{textOrUnavailable(module.scope)}</td>
                <td>
                  <span className={selectedEnabled ? 'pill pill-green' : 'pill'}>{selectedEnabled ? 'Enabled' : 'Disabled'}</span>
                </td>
                <td>{formatSelectedScope(module, selectedPlatform)}</td>
                <td>
                  {textOrUnavailable(module.assignment_source)}
                  {sourceDetail ? <><br /><small>{sourceDetail}</small></> : null}
                </td>
                <td>{textOrUnavailable(module.effective_priority)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function RecordList({ records, titleKey }: { records: unknown[]; titleKey: string }) {
  return (
    <div className="record-list">
      {records.slice(0, 12).map((record, index) => {
        const item = asRecord(record);
        const metaEntries: Array<[string, unknown]> = [
          ['ID', item.id],
          ['Version', item.version],
          ['Actor', item.actor],
          ['Resource', item.resource_kind || item.kind],
          ['Created', item.created_at || item.timestamp],
          ['Updated', item.updated_at],
        ];
        const meta = metaEntries.filter(([, value]) => text(value));
        return (
          <article key={textOrUnavailable(item.id) + index} className="record-card">
            <strong>{textOrUnavailable(item[titleKey])}</strong>
            {meta.length ? (
              <dl className="record-meta">
                {meta.map(([key, value]) => (
                  <div key={key}>
                    <dt>{key}</dt>
                    <dd>{textOrUnavailable(value)}</dd>
                  </div>
                ))}
              </dl>
            ) : <p>No summary metadata returned.</p>}
          </article>
        );
      })}
    </div>
  );
}

function JsonBlock({ value, empty, redactPromptContent = false }: { value: unknown; empty: string; redactPromptContent?: boolean }) {
  if (value === undefined || value === null) {
    return <EmptyState title="Unavailable" description={empty} />;
  }
  return <pre className="json-preview">{redactedJson(value, { redactPromptContent })}</pre>;
}

function UnavailableNotice({ errors, loading }: { errors: Record<string, string>; loading: boolean }) {
  const entries = Object.entries(errors);
  if (!loading && entries.length === 0) return null;
  return (
    <section className={entries.length ? 'notice notice-warning' : 'notice'}>
      {loading ? <strong>Loading live management API data…</strong> : <strong>Some live data is unavailable</strong>}
      {entries.length ? (
        <ul>
          {entries.map(([key, message]) => <li key={key}>{key}: {message}</li>)}
        </ul>
      ) : null}
    </section>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="empty-state">
      <strong>{title}</strong>
      <p>{description}</p>
    </div>
  );
}

function normalizeTab(value?: string | null): TabId {
  const normalized = value === 'config' ? 'configuration' : value;
  return tabs.some(tab => tab.id === normalized) ? normalized as TabId : 'overview';
}

function firstSessionID(sessionsPayload: unknown) {
  const first = getArray(asRecord(sessionsPayload).sessions)[0];
  return text(asRecord(first).id);
}

function configIDFrom(persona: unknown, config: unknown) {
  return text(asRecord(asRecord(persona).control_plane).persona_config_id) || text(asRecord(asRecord(config).manifest).id);
}

function personaConfigVersionSummaries(sources: PersonaWorkbenchSources): PersonaConfigVersionSummary[] {
  const manifest = asRecord(asRecord(sources.config).manifest);
  const status = asRecord(sources.status);
  const publishedVersion = numberValue(manifest.published_version) || numberValue(asRecord(status.published).version);
  const currentVersion = numberValue(manifest.current_version);
  return getArray(asRecord(sources.versions).versions)
    .map(version => asRecord(version))
    .map(version => {
      const versionNumber = numberValue(version.version);
      const changelog = text(version.changelog);
      const createdAt = text(version.created_at);
      const createdBy = text(version.created_by) || 'unknown actor';
      const validation = asRecord(version.validation_result);
      const validationText = validation.valid === true
        ? 'Validation passed'
        : validation.valid === false
          ? 'Validation failed'
          : 'Validation result unavailable';
      const statusText = versionNumber === publishedVersion
        ? 'Published'
        : versionNumber === currentVersion
          ? 'Saved draft version'
          : 'Saved version';
      const title = `v${versionNumber || text(version.version) || '?'} · ${changelog || statusText}`;
      const summary = [
        changelog || 'No changelog returned by version API',
        statusText,
        createdAt ? `created ${createdAt}` : 'creation time unavailable',
        `by ${createdBy}`,
        validationText,
      ].join(' · ');
      const sourceHash = text(version.source_hash);
      return {
        version: versionNumber,
        title,
        summary,
        meta: [
          `version ${versionNumber || text(version.version) || '?'}`,
          sourceHash ? `source ${sourceHash.slice(0, 8)}` : 'source hash unavailable',
        ],
      };
    })
    .filter(version => version.version > 0)
    .sort((left, right) => right.version - left.version);
}

function draftHasChanges(sources: PersonaWorkbenchSources) {
  const status = asRecord(sources.status);
  const assignments = asRecord(sources.promptAssignments);
  return status.draft_changed === true || assignments.draft_changed === true;
}

function draftLifecycleRows(sources: PersonaWorkbenchSources, t: Translate): Array<[string, string]> {
  const config = asRecord(sources.config);
  const manifest = asRecord(config.manifest);
  const draft = asRecord(config.draft);
  return [
    [t('modules.personaWorkbench.drawers.rows.draftRevision'), textOrUnavailable(draft.revision)],
    [t('modules.personaWorkbench.drawers.rows.updatedAt'), textOrUnavailable(draft.updated_at)],
    [t('modules.personaWorkbench.drawers.rows.updatedBy'), textOrUnavailable(draft.updated_by)],
    [t('modules.personaWorkbench.drawers.rows.publishedVersion'), versionText(manifest.published_version)],
  ];
}

function versionText(value: unknown) {
  const raw = text(value);
  if (!raw || raw === '0') return 'Unpublished';
  return `v${raw}`;
}

function formatModuleName(value: string) {
  return value
    .toLowerCase()
    .split(/[_-]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function scopeOptions(platforms: string[], selectedPlatform: string) {
  if (!selectedPlatform || platforms.includes(selectedPlatform)) {
    return platforms;
  }
  return [...platforms, selectedPlatform];
}

function formatScopeLabel(value: unknown) {
  const raw = text(value).trim();
  if (!raw || raw === 'default') return 'Default';
  return raw;
}

function promptModuleSelectedEnabled(module: Record<string, unknown>) {
  if ('selected_scope_enabled' in module) {
    return boolValue(module.selected_scope_enabled);
  }
  return boolValue(module.enabled);
}

function formatSelectedScope(module: Record<string, unknown>, selectedPlatform: string) {
  return formatScopeLabel(text(module.selected_scope) || selectedPlatform);
}

function promptModuleSourceDetail(module: Record<string, unknown>) {
  const detail = text(module.source_detail);
  if (detail) return detail;
  const platform = text(module.platform);
  if (platform) return `artifact_ref.platform=${platform}`;
  return '';
}

function textOrUnavailable(value: unknown) {
  return text(value) || 'Unavailable';
}

function text(value: unknown) {
  if (value === undefined || value === null || value === '') return '';
  return String(value);
}

function numberValue(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function boolValue(value: unknown) {
  return value === true || value === 'true';
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

function getArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
